import {
  Box,
  BoxProps,
  IconButton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useState } from "react";
import Card from "src/components/Card";
import { RiShareBoxLine } from "react-icons/ri";
import { useIntl } from "react-intl";
import { AiOutlineReload } from "react-icons/ai";
import { BiEdit } from "react-icons/bi";
import { BiArchiveOut } from "react-icons/bi";
import { BiArchive } from "react-icons/bi";
import { BiCopy } from "react-icons/bi";
import { AiFillEye } from "react-icons/ai";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteButton from "../DeleteModel";

type ItemCardProps = {
  resort?: () => void | Promise<void>;
  remove?: () => void;
  edit?: () => void;
  archive?: () => void;
  view?: () => void;
  unarchive?: () => void;
  douplicate?: () => void;
  share?: () => void;
  isSharedWithMe?: boolean;
  divChild?: any;
  permission?: any;
  isPlaying?: any;
  onPlay?: any;
  onPause?: any;
} & BoxProps;

const ItemCard: React.FC<ItemCardProps> = ({
  children,
  resort,
  remove,
  edit,
  archive,
  unarchive,
  onClick,
  view,
  divChild,
  douplicate,
  share,
  isSharedWithMe,
  permission,
  isPlaying,
  onPlay,
  onPause,
  ...props
}) => {
  const color = useColorModeValue("gray.700", "gray.800");
  const canUpdate = permission === "update";
  const { formatMessage } = useIntl();
  const [restoreLoading, setRestoreLoading] = useState(false);

  return (
    <>
      <Card
        my="2"
        _hover={{ bg: "gray.200", cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          onClick && onClick(e);
        }}
        {...props}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {divChild ? (
          <Box display="inline-block" color={color}>
            {children}
          </Box>
        ) : (
          <Text display="inline-block" color={color}>
            {children}
          </Text>
        )}
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {isSharedWithMe && (
            <Text fontSize="sm" color="gray.500" mr={3}>
              {formatMessage({ id: "shared" })}
            </Text>
          )}
          {douplicate ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                douplicate();
              }}
              variant="link"
              aria-label="duplicate"
              fontSize="20px"
              minW="32px"
              icon={<BiCopy />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {(!isSharedWithMe || (isSharedWithMe && canUpdate)) && edit ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                edit();
              }}
              color="brand.800"
              variant="link"
              aria-label="edit"
              fontSize="20px"
              minW="32px"
              icon={<BiEdit />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {(!isSharedWithMe || (isSharedWithMe && canUpdate)) && resort ? (
            <IconButton
              minW="32px"
              isLoading={restoreLoading}
              onClick={(e) => {
                e.stopPropagation();
                setRestoreLoading(true);
                try {
                  const result = resort();
                  if (result && result instanceof Promise) {
                    result
                      .then(() => {
                        setRestoreLoading(false);
                      })
                      .catch(() => {
                        setRestoreLoading(false);
                      });
                  } else {
                    setRestoreLoading(false);
                  }
                } catch (e) {
                  setRestoreLoading(false);
                }
              }}
              color="brand.800"
              variant="link"
              aria-label="resort"
              fontSize="20px"
              icon={<AiOutlineReload />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {view ? (
            <IconButton
              minW="32px"
              onClick={(e) => {
                e.stopPropagation();
                view();
              }}
              color="brand.800"
              variant="link"
              aria-label="view"
              fontSize="20px"
              icon={<AiFillEye />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {(!isSharedWithMe || (isSharedWithMe && canUpdate)) && archive ? (
            <IconButton
              minW="32px"
              onClick={(e) => {
                e.stopPropagation();
                archive();
              }}
              color="brand.800"
              variant="link"
              aria-label="archive"
              fontSize="20px"
              icon={<BiArchive />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {(!isSharedWithMe || (isSharedWithMe && canUpdate)) && unarchive ? (
            <IconButton
              minW="32px"
              onClick={(e) => {
                e.stopPropagation();
                unarchive();
              }}
              color="brand.800"
              variant="link"
              aria-label="unarchive"
              fontSize="20px"
              icon={<BiArchiveOut />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          ) : null}
          {!isSharedWithMe && share && (
            <IconButton
              onClick={(e: any) => {
                e.stopPropagation();
                share();
              }}
              variant="link"
              aria-label="share"
              fontSize="20px"
              minW="32px"
              icon={<RiShareBoxLine className="remove-on-print" />}
              h="32px"
              w="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            />
          )}
          {onPlay && onPause ? (
            isPlaying ? (
              <IconButton
                minW="32px"
                onClick={(e) => {
                  e.stopPropagation();
                  onPause();
                }}
                color="brand.800"
                variant="link"
                aria-label="pause"
                fontSize="20px"
                icon={<PauseIcon />}
                h="32px"
                w="32px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              />
            ) : (
              <IconButton
                minW="32px"
                onClick={(e) => {
                  e.stopPropagation();
                  onPlay();
                }}
                color="brand.800"
                variant="link"
                aria-label="play"
                fontSize="20px"
                icon={<PlayArrowIcon />}
                h="32px"
                w="32px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              />
            )
          ) : null}
          {remove && !isSharedWithMe ? <DeleteButton remove={remove} /> : null}
        </Box>
      </Card>
    </>
  );
};
export default ItemCard;
